import type { UserDataServiceRequestType } from '../data/schema/UserDataServiceRequestType';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { makePostRequest } from 'owa-ows-gateway';
import { trace } from 'owa-trace';
import type {
    DataProviderFilter,
    Suggestion,
    UserDataServiceRequest,
    UserDataServiceResponse,
    UserInputContent,
} from '../data/schema/UserDataServiceSchema';

const USER_DATA_SERVICE_URL_ENDPOINT: string = 'ows/v1.5/UserDataStore/userdata';

/**
 * Get suggestion response from the UserDataService based on the input.
 */
export default function getSuggestionsFromUserDataService(
    input: string,
    requestType: UserDataServiceRequestType,
    callInitOps: Boolean,
    dataProviderFilter?: DataProviderFilter,
    customHeaders?: any
): Promise<Suggestion[]> {
    // Construct the UserDataService request.
    const request = <UserDataServiceRequest>{
        requestType: requestType,
        userInputContent: <UserInputContent>{
            textInput: input,
            dataProviderFilter: dataProviderFilter,
            shouldInitSubstrateSearch: callInitOps,
        },
    };

    return makePostRequest(
        USER_DATA_SERVICE_URL_ENDPOINT,
        request,
        undefined /* correlationId */,
        true /* returnFullResponse */,
        customHeaders /* customHeaders */,
        undefined /* throwServiceError */,
        undefined /* sendPayloadAsBody */,
        undefined /* includeCredentials */,
        'getUserDataServiceInfo' /* actionName */
    )
        .then(async response => {
            if (isSuccessStatusCode(response?.status)) {
                const userDataServiceResponse = await parseUserDataServiceResponse(response);
                if (userDataServiceResponse?.suggestions) {
                    return Promise.resolve(userDataServiceResponse.suggestions);
                } else {
                    return Promise.resolve([]);
                }
            } else {
                // Throw an error for logging in the catch() clause.
                throw new Error(
                    '[User Data Service] Failed to get suggestions, error code: ' + response.status
                );
            }
        })
        .catch(error => {
            trace.warn(error);
            return Promise.reject(error);
        });
}

/**
 * Parse the UserDataItem mutate response from the server.
 */
async function parseUserDataServiceResponse(response: Response): Promise<UserDataServiceResponse> {
    const data = await response.json();
    return Promise.resolve(<UserDataServiceResponse>{
        inputContent: data.inputContent,
        suggestions: data.suggestions,
    });
}
